import { useMutation, useQuery } from "@tanstack/react-query";
import { ModulePrefixes } from "enums";
import { printError, useUtilities } from "helpers";
import { axiosInstance } from "helpers";
import { t } from "i18next";
import { useEffect } from "react";
import { updateContentRequestItemInContentRequest } from "redux/reducers/templateLibrary/contentRequests";
import {
  addDefaultSiteContentRequestItem,
  addTemplateContentRequestItem,
  deleteDefaultSiteContentRequestItem,
  deleteTemplateContentRequest,
  getAllDefaultSiteContentRequestsItem,
  getAllTemplatecontentRequestsItem,
  updateTemplateContentRequest,
} from "redux/reducers/templateLibrary/contentRequestsItem";

export async function getContentRequestsItem() {
  return axiosInstance
    .post("showlibrarytemplatecontentlist", {
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetContentRequestsItem() {
  return useQuery(["contentRequestsItem"], getContentRequestsItem);
}

export function useContentRequestsItem() {
  const {
    dispatch,
    contentRequestsItem: { contentRequestsItem, loaded },
  } = useUtilities();
  const { data, isLoading, error } = useGetContentRequestsItem();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) dispatch(getAllTemplatecontentRequestsItem(data.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, loaded]);

  return { data: contentRequestsItem, isLoading, error };
}

export async function addContentRequestItem(request) {
  return axiosInstance
    .post("addcontentlistlibrarytemplate", {
      ...request,
      api_key: localStorage.getItem("apikey"),
      wpsl_tem: localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
}

export function useAddContentRequestItem(redirect = true) {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(addContentRequestItem, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addTemplateContentRequestItem(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequestItem.api.success.added"),
        });
        if (redirect) {
          navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-items/${data.data.id}`);
        }
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.response.data.message),
      });
    },
  });
}

export async function updateContentRequestItem(request) {
  return axiosInstance
    .post("editlibrarytemplatecontentlist", {
      api_key: localStorage.getItem("apikey"),
      ...request,
    })
    .then((res) => res.data)
}

export function useUpdateContentRequestItem() {
  const { dispatch, notification } = useUtilities();
  return useMutation(updateContentRequestItem, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(updateTemplateContentRequest(data.data));
        dispatch(updateContentRequestItemInContentRequest(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequestItem.api.success.updated"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.response.data.message),
      });
    },
  });
}

export async function deleteContentRequestItem(id) {
  return axiosInstance
    .post("deletecontentlistlibrarytemplate", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => ({ ...res.data, id }))
}

export function useDeleteContentRequestItem(redirect = false) {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(deleteContentRequestItem, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteTemplateContentRequest(data.id));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequestItem.api.success.deleted"),
        });
        if (redirect) navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-items`);
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(error.response.data.message),
      });
    },
  });
}

export async function getDefaultContentRequestItem() {
  return axiosInstance
    .post("getwpslcontentlisttemp", {
      api_key: localStorage.getItem("apikey"),
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetDefaultContentRequestItem() {
  return useQuery(["defaultContentRequestItem"], getDefaultContentRequestItem);
}

export function useDefaultContentRequestItem() {
  const {
    dispatch,
    contentRequestsItem: {
      defaultSite: { contentRequestsItem, loaded },
    },
  } = useUtilities();
  const { data, isLoading, error } = useGetDefaultContentRequestItem();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) dispatch(getAllDefaultSiteContentRequestsItem(data.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, error, loaded]);

  return { data: contentRequestsItem, isLoading: isLoading || !loaded, error };
}

export async function addDefaultContentRequestItem(id) {
  return axiosInstance
    .post("adddefaulttemplatecontentlist", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddDefaultContentRequestItem() {
  const { dispatch, notification } = useUtilities();
  return useMutation(addDefaultContentRequestItem, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addDefaultSiteContentRequestItem(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequestItem.api.success.added"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: error.message,
      });
    },
  });
}

export async function deleteDefaultContentRequestItem(id) {
  return axiosInstance
    .post("defaultcontentlistremove", {
      api_key: localStorage.getItem("apikey"),
      id,
    })
    .then((res) => ({ ...res.data, id }))
    .catch((err) => new Error(err));
}

export function useDeleteDefaultContentRequestItem() {
  const { dispatch, notification } = useUtilities();
  return useMutation(deleteDefaultContentRequestItem, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteDefaultSiteContentRequestItem(data.id));
        notification({
          type: "success",
          message: t("libraryTemplate.contentRequestItem.api.success.deleted"),
        });
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: error.message,
      });
    },
  });
}
